import React, { useState, forwardRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Check, ChevronLeft, ChevronRight, Loader } from "lucide-react";
import styles from "./NotificationsDropdown.module.css";
import NotificationItem from "./NotificationItem/NotificationItem";

const NotificationsDropdownComponent = (
    { notifications, loading, markAsRead, markAsUnread, deleteNotifications, refreshUnreadNotifications, type = "navbar", onClose },
    ref
) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [paginationInfo, setPaginationInfo] = useState({ next: null, previous: null, count: 0 });
    const notificationsPerPage = 10;
    const navigate = useNavigate();

    const hasUnreadNotifications = notifications.some(notification => !notification.isRead);
    const unreadCount = notifications.filter(n => !n.isRead).length;

    // Fetch notifications when page changes
    useEffect(() => {
        const fetchPage = async () => {
            if (refreshUnreadNotifications) {
                const response = await refreshUnreadNotifications(currentPage);
                if (response) {
                    setPaginationInfo({
                        next: response.next,
                        previous: response.previous,
                        count: response.count
                    });
                }
            }
        };
        fetchPage();
    }, [currentPage]);

    const handleNotificationClick = (notification, e) => {
        navigate(`/inbox/${notification.id}?instanceId=${notification.notificationsId}`);
        markAsRead([notification.id])
        onClose?.();
    };

    const handleMarkAllAsRead = async () => {
        if (hasUnreadNotifications) {
            const unreadIds = notifications
                .filter(notification => !notification.isRead)
                .map(notification => notification.id);
            if (unreadIds.length > 0) {
                await markAsRead(unreadIds, currentPage);

                let targetPage = currentPage;
                let response = await refreshUnreadNotifications(targetPage);
                if (!response && targetPage > 1) {
                    targetPage = targetPage - 1;
                    response = await refreshUnreadNotifications(targetPage);
                }

                if (response) {
                    setCurrentPage(targetPage);
                    setPaginationInfo({
                        next: response.next,
                        previous: response.previous,
                        count: response.count
                    });
                }
            }
        }
    };

    const handleNextPage = () => {
        if (paginationInfo.next) {
            setCurrentPage(prev => prev + 1);
        }
    };

    const handlePreviousPage = () => {
        if (paginationInfo.previous) {
            setCurrentPage(prev => prev - 1);
        }
    };

    if (loading) {
        return (
            <div className={styles.NotificationsDropdown}>
                <div className={styles.loading}>
                    <Loader className={styles.loadingIcon} size={20} />
                    <span>Loading notifications...</span>
                </div>
            </div>
        );
    }

    return (
        <div ref={ref} className={styles.NotificationsDropdown}>
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <span className={styles.title}>
                        <Bell size={18} className={styles.bellIcon} />
                        Notifications
                    </span>
                </div>
                {hasUnreadNotifications && (
                    <button
                        className={styles.markAllButton}
                        onClick={handleMarkAllAsRead}
                        title="Mark all as read"
                    >
                        <Check size={16} />
                        <span>Mark all read</span>
                    </button>
                )}
            </div>

            <div className={styles.notificationsList}>
                {notifications.length > 0 ? (
                    notifications.map((notification) => (
                        <NotificationItem
                            key={notification.id}
                            notification={notification}
                            type={type}
                            onNotificationClick={() => handleNotificationClick(notification)}
                            onToggleReadStatus={() =>
                                notification.isRead
                                    ? markAsUnread([notification.id])
                                    : markAsRead([notification.id])
                            }
                            onDelete={() => deleteNotifications([notification.id])}
                            onAction={notification.requiresAction}
                        />
                    ))
                ) : (
                    <div className={styles.emptyState}>
                        <Bell size={24} className={styles.emptyIcon} />
                        <span>No notifications available</span>
                    </div>
                )}
            </div>

            {(paginationInfo.next || paginationInfo.previous) && (
                <div className={styles.paginationContainer}>
                    {paginationInfo.previous && (
                        <button
                            className={styles.paginationLink}
                            onClick={handlePreviousPage}
                            disabled={loading}
                        >
                            <ChevronLeft size={16} />
                            <span>Previous</span>
                        </button>
                    )}
                    {paginationInfo.previous && paginationInfo.next && (
                        <span className={styles.buttonSpacer}>|</span>
                    )}
                    {paginationInfo.next && (
                        <button
                            className={styles.paginationLink}
                            onClick={handleNextPage}
                            disabled={loading}
                        >
                            <span>Next</span>
                            <ChevronRight size={16} />
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

const NotificationsDropdown = forwardRef(NotificationsDropdownComponent);

export default NotificationsDropdown;